import { FC } from "react";
import { useFragment } from "react-relay";
import { Link } from "react-router-dom";
import { graphql } from "relay-runtime";
import {
  AssetLabel,
  classApr,
  Decimal,
  Fiat,
  formatApr,
  i18n,
  IconDenom,
  Slider,
} from "rujira.ui";
import { AngleRight } from "rujira.ui/src/components/icons/Icons";
import { Subscription } from "../../services/useNodeSubscription";
import { PerpsPoolAccountFragment$key } from "./__generated__/PerpsPoolAccountFragment.graphql";
import { PerpsPoolFragment$key } from "./__generated__/PerpsPoolFragment.graphql";
import { PerpsPoolRowFragment$key } from "./__generated__/PerpsPoolRowFragment.graphql";
import { Meta, StrategyContainer } from "./Common";
import { PerpsPoolBalance } from "./PerpsPoolBalance";
import { PerpsDeposit } from "./PerpsPoolDeposit";

const row = graphql`
  fragment PerpsPoolRowFragment on PerpsPool {
    id
    address
    baseAsset {
      metadata {
        symbol
      }
    }
    quoteAsset {
      asset
      chain
      type
      metadata {
        symbol
      }
    }
    stats {
      lpApr {
        value
        status
      }
    }
    liquidity {
      locked {
        valueUsd
      }
      unlocked {
        valueUsd
      }
      total {
        valueUsd
      }
    }
  }
`;

const fragment = graphql`
  fragment PerpsPoolFragment on PerpsPool {
    id
    address
    baseAsset {
      metadata {
        symbol
      }
    }
    quoteAsset {
      ...msgAssetFragment
      asset
      chain
      type
      metadata {
        symbol
      }
    }
    stats {
      lpApr {
        value
        status
      }
      xlpApr {
        value
        status
      }
      lpRoi {
        value
        status
      }
      xlpRoi {
        value
        status
      }
      risk
      sharpeRatio
    }
    liquidity {
      total {
        amount
        asset {
          metadata {
            decimals
          }
        }
        valueUsd
      }
      locked {
        amount
        asset {
          metadata {
            decimals
          }
        }
        valueUsd
      }
    }
  }
`;

const accountFragment = graphql`
  fragment PerpsPoolAccountFragment on Account {
    address
    strategies {
      ... on PerpsAccount {
        ...PerpsPoolBalanceFragment
        account
        pool {
          address
        }
      }
    }
  }
`;

const subscription = graphql`
  subscription PerpsPoolSubscription($id: ID!) {
    node(id: $id) {
      ... on PerpsPool {
        stats {
          lpApr {
            value
            status
          }
          xlpApr {
            value
            status
          }
          lpRoi {
            value
            status
          }
          xlpRoi {
            value
            status
          }
          risk
          sharpeRatio
        }
        liquidity {
          total {
            amount
            valueUsd
          }
          locked {
            amount
            valueUsd
          }
          unlocked {
            valueUsd
          }
        }
      }
    }
  }
`;

export const PerpsPoolRow: FC<{ k: PerpsPoolRowFragment$key }> = ({ k }) => {
  const { id, baseAsset, quoteAsset, liquidity, stats } = useFragment(row, k);
  const url = `/strategies/derivatives/perps/${baseAsset.metadata.symbol}-${quoteAsset.metadata.symbol}`;

  return (
    <tr className="pointer">
      <Subscription id={id} subscription={subscription} />
      <td>
        <Link
          to={url}
          className="flex ai-c mt-0.5 no-underline color-white w-full">
          <div className="lp">
            <IconDenom denom={baseAsset.metadata.symbol} />
            <IconDenom denom={quoteAsset.metadata.symbol} />
          </div>
          <h3 className="condensed fs-22 lh-22 fs-lg-28 lh-lg-28 fw-400 mb-0 ml-1 nowrap">
            {baseAsset.metadata.symbol} <span className="color-grey">/</span>{" "}
            <AssetLabel
              asset={quoteAsset}
              Container={({ children }) => (
                <small className="color-grey fs-14 fs-lg-16">{children}</small>
              )}
            />
          </h3>
        </Link>
      </td>
      <td>
        <Link to={url} className="flex dir-c no-underline color-white w-full">
          <Fiat
            amount={BigInt(liquidity.total.valueUsd)}
            symbol="$"
            decimals={8}
            className="fs-16 fs-md-18 color-white"
          />
          <div className="flex ai-c mt-0.5">
            <Decimal
              amount={BigInt(liquidity.unlocked.valueUsd || 0)}
              className="fs-12 fs-md-14 condensed color-grey"
              symbol="LP"
            />
            <span className="block mx-1 color-grey opacity-8">/</span>
            <Decimal
              amount={BigInt(liquidity.locked.valueUsd)}
              className="fs-12 fs-md-14 condensed color-grey"
              symbol="xLP"
            />
          </div>
        </Link>
      </td>
      <td className="text-right">
        <span className={classApr(stats.lpApr)}>{formatApr(stats.lpApr)}</span>
      </td>
      <td className="text-right">
        <Link to={url} className="block w-full text-right">
          <i18n.div className="tag tag--purple">Perps LP / xLP</i18n.div>
        </Link>
      </td>
      <td className="w-3">
        <Link to={url} className="block w-full text-right">
          <AngleRight className="w-3 h-3 color-grey" />
        </Link>
      </td>
    </tr>
  );
};

export const PerpsPool: FC<{
  k: PerpsPoolFragment$key;
  a?: PerpsPoolAccountFragment$key;
}> = ({ k, a }) => {
  const account = useFragment(accountFragment, a);
  const data = useFragment(fragment, k);

  const strategy = account?.strategies.find((strategy) => {
    return strategy.pool?.address === data.address;
  });

  return (
    <StrategyContainer>
      <Meta
        title={`Rujira Perps ${data.baseAsset.metadata.symbol}/${data.quoteAsset.metadata.symbol}`}
        description=""
      />
      <Subscription id={data.id} subscription={subscription} />
      <div className="flex ai-c wrap">
        <h1 className="h1 mb-0 mr-2">
          {data.baseAsset.metadata.symbol}{" "}
          <small className="color-grey">/</small>{" "}
          <AssetLabel
            asset={data.quoteAsset}
            Container={({ children }) => (
              <small className="color-grey fs-20 fs-lg-28">{children}</small>
            )}
          />
        </h1>
        <div className="lp big mr-3">
          <IconDenom denom={data.baseAsset.metadata.symbol} />
          <IconDenom denom={data.quoteAsset.metadata.symbol} />
        </div>
        <i18n.div className="tag tag--lg tag--purple my-1.5">
          Perps LP / xLP
        </i18n.div>
      </div>
      <div className="row pad wrap mt-1">
        <div className="col-12 col-md-8 mt-2">
          <div className="row pad wrap">
            <div className="col-12 col-sm-6 col-lg-4">
              <div className="card h-full p-3 flex dir-c">
                <i18n.h3 className="fs-16 lh-22 fw-400 color-grey">
                  Total Liquidity
                </i18n.h3>
                <Fiat
                  symbol="$"
                  amount={BigInt(data.liquidity.total.valueUsd)}
                  decimals={data.liquidity.total.asset.metadata.decimals}
                  className="fs-22 fs-md-32 condensed fw-500 mt-1"
                />
                <div className="flex ai-c mt-1">
                  <IconDenom
                    denom={data.quoteAsset.metadata.symbol}
                    className="h-2 w-2 mr-1"
                  />
                  <Decimal
                    amount={BigInt(data.liquidity.total.amount)}
                    round={4}
                    className="fs-16 fs-md-18 lh-16 lh-md-18 condensed color-grey"
                  />
                </div>
              </div>
            </div>
            <div className="col-12 col-sm-6 col-lg-4 mt-2 mt-sm-0">
              <div className="card h-full p-3 flex dir-c">
                <i18n.h3 className="fs-16 lh-22 fw-400 color-grey">
                  Locked Liquidity
                </i18n.h3>
                <Fiat
                  symbol="$"
                  amount={BigInt(data.liquidity.locked.valueUsd)}
                  decimals={data.liquidity.locked.asset.metadata.decimals}
                  className="fs-22 fs-md-32 condensed fw-500 mt-1"
                />
                <div className="flex ai-c mt-1">
                  <IconDenom
                    denom={data.quoteAsset.metadata.symbol}
                    className="h-2 w-2 mr-1"
                  />
                  <Decimal
                    amount={BigInt(data.liquidity.locked.amount)}
                    round={4}
                    className="fs-16 fs-md-18 lh-16 lh-md-18 condensed color-grey"
                  />
                </div>
              </div>
            </div>
            <div className="col-12 col-sm-6 col-lg-4 mt-2 mt-sm-4 mt-lg-0">
              <div className="card h-full p-3 flex dir-c">
                <i18n.h3 className="fs-16 lh-22 fw-400 color-grey">
                  7-day ROI
                </i18n.h3>
                <div className="flex ai-b">
                  <p
                    className="fs-16 fs-md-18 condensed color-grey"
                    style={{ width: 35 }}>
                    LP
                  </p>
                  <p className="fs-22 fs-md-32 condensed fw-500 mt-1">
                    <span className={classApr(data.stats.lpRoi)}>
                      {formatApr(data.stats.lpRoi)}
                    </span>
                  </p>
                </div>
                <div className="flex ai-b">
                  <p
                    className="fs-16 fs-md-18 condensed color-grey"
                    style={{ width: 35 }}>
                    xLP
                  </p>
                  <p className="fs-22 fs-md-32 condensed fw-500 mt-1">
                    <span className={classApr(data.stats.xlpRoi)}>
                      {formatApr(data.stats.xlpRoi)}
                    </span>
                  </p>
                </div>
              </div>
            </div>
            <div className="col-12 col-sm-6 col-lg-4 mt-2 mt-sm-4">
              <div className="card h-full p-3 flex dir-c">
                <i18n.h3 className="fs-16 lh-22 fw-400 color-grey">
                  7-day APR
                </i18n.h3>
                <div className="flex ai-b">
                  <p
                    className="fs-16 fs-md-18 condensed color-grey"
                    style={{ width: 35 }}>
                    LP
                  </p>
                  <p className="fs-22 fs-md-32 condensed fw-500 mt-1">
                    <span className={classApr(data.stats.lpApr)}>
                      {formatApr(data.stats.lpApr)}
                    </span>
                  </p>
                </div>
                <div className="flex ai-b">
                  <p
                    className="fs-16 fs-md-18 condensed color-grey"
                    style={{ width: 35 }}>
                    xLP
                  </p>
                  <p className="fs-22 fs-md-32 condensed fw-500 mt-1">
                    <span className={classApr(data.stats.xlpApr)}>
                      {formatApr(data.stats.xlpApr)}
                    </span>
                  </p>
                </div>
              </div>
            </div>
            <div className="col-12 col-sm-6 col-lg-4 mt-2 mt-sm-4">
              <div className="card h-full p-3 flex dir-c">
                <i18n.h3 className="fs-16 lh-22 fw-400 color-grey">
                  Sharpe Ratio
                </i18n.h3>
                <div className="flex ai-b mt-3 mb-2">
                  <Slider
                    min={0}
                    max={3}
                    value={Number(data.stats.sharpeRatio)}
                    marks={[0, 1, 2, 3]}
                    renderThumb={(props) => {
                      const { key } = props;
                      const sharpeRatio = Number(data.stats.sharpeRatio);
                      const percentage = sharpeRatioPercentage(sharpeRatio);
                      const amount = sharpeRatioAmount(sharpeRatio);

                      return (
                        <div
                          key={key}
                          className="perps__slider-value"
                          style={{
                            left: `${percentage}%`,
                            transform: `translateX(-${percentage}%)`,
                          }}>
                          <Decimal amount={amount} decimals={1} round={1} />
                        </div>
                      );
                    }}
                    renderTrack={(props) => {
                      const { key, className, style, ...otherProps } = props;
                      const percentage = sharpeRatioPercentage(
                        Number(data.stats.sharpeRatio)
                      );

                      if (props.key === "slider__track-0") {
                        return (
                          <div
                            {...otherProps}
                            key={key}
                            className={`${className} perps__slider-track`}
                            style={{ width: `${percentage}%` }}
                          />
                        );
                      }

                      return (
                        <div
                          {...otherProps}
                          key={key}
                          className={`${className} perps__slider-track`}
                        />
                      );
                    }}
                    renderMark={(props) => (
                      <span
                        key={props.key}
                        className="perps__slider color-grey fs-14">
                        {props.key?.toString()}
                      </span>
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="col-12 col-sm-6 col-lg-4 mt-2 mt-sm-4">
              <div className="card h-full p-3 flex dir-c">
                <i18n.h3 className="fs-16 lh-22 fw-400 color-grey">
                  Risk
                </i18n.h3>
                <div className="flex ai-b mt-2">
                  <div className="perps__risk" data-risk={data.stats.risk}>
                    <div />
                    <div />
                    <div />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 mt-2 mt-sm-4">
              <PerpsDeposit address={data.address} quote={data.quoteAsset} />
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4 mt-2 gradient-card-container">
          <PerpsPoolBalance address={data.address} k={strategy} />
        </div>
      </div>
      <hr className="hr mt-8 mb-8 opacity-4" />

      <PoolSummary quoteSymbol={data.quoteAsset.metadata.symbol} />
    </StrategyContainer>
  );
};

const PoolSummary: FC<{ quoteSymbol: string }> = ({ quoteSymbol }) => {
  return (
    <>
      <i18n.h3 className="h3">Strategy Details</i18n.h3>
      <h4 className="h4">
        {i18n.t("​Strategy")}: Liquidity Provision (LP & xLP)
      </h4>
      <p className="p">
        Provide {quoteSymbol} liquidity to earn trading fees and protocol
        rewards.
      </p>
      <p className="p">
        Deposit {quoteSymbol} to provide liquidity for traders on RUJI Perps. As
        an LP, you earn a share of trading fees, borrow fees, and liquidation
        rewards. When traders lose, LPs gain; when traders win, LPs cover their
        profits. You can choose between standard LP tokens or xLP tokens for
        higher rewards with a longer lockup.
      </p>
      <h4 className="h4 mt-5">How It Works</h4>
      <ul>
        <li className="p">
          <b>Deposit {quoteSymbol}</b> to the liquidity pool to become an LP.
        </li>
        <li className="p">
          <b>Earn rewards</b> from trading fees, borrow fees, and liquidation
          events.
        </li>
        <li className="p">
          <b>LPs take on risk:</b> When traders lose, LPs gain; when traders
          win, LPs cover their profits.
        </li>
        <li className="p">
          <b>LP tokens</b> can be withdrawn at any time (subject to available
          liquidity).
        </li>
        <li className="p">
          <b>xLP tokens</b> offer higher rewards but require a 45-day lockup
          period.
        </li>
      </ul>
      <h4 className="h4 mt-5">Withdrawals</h4>
      <ul>
        <li className="p">
          <b>LP tokens:</b> Withdrawals are subject to a 24-hour delay (if
          liquidity is available).
        </li>
        <li className="p">
          <b>xLP tokens:</b> 45-day unbinding period before withdrawal is
          possible.
        </li>
      </ul>
      <h4 className="h4 mt-5">Revenue</h4>
      <ul>
        <li className="p">
          <b>Trading fees:</b> Earned from every trade on the platform.
        </li>
        <li className="p">
          <b>Borrow fees:</b> Paid by traders for using leverage.
        </li>
        <li className="p">
          <b>Liquidation rewards:</b> Earned when traders are liquidated or
          close positions at a loss.
        </li>
      </ul>
      <h4 className="h4 mt-5">Risk</h4>
      <ul>
        <li>
          <p className="p">
            <b>Price risk:</b> Liquidity providers act as the counterparty to
            every trade on the platform. This means when a trader opens a long,
            it's as if the LP pool is opening a short of the same size—and vice
            versa. If the market moves in the trader’s favor (e.g., a long
            position profits as prices rise), the LP pool absorbs the loss. This
            can lead to principal impairment, especially in markets with
            unbalanced net open interest (e.g., more longs than shorts).
          </p>
          <p className="p">
            The protocol attempts to minimize price risk through: Delta-neutral
            incentives (funding rate payments, basis trading), Dynamic borrow
            fees to balance supply and demand, and Internal controls on max gain
            collateral locking per position.
          </p>
          <p className="p">
            However, if these mechanisms fail to fully offset directional
            risk—especially in cases of extreme market movements or persistent
            imbalance in trader positions—LPs and xPL holders remain exposed to
            substantial losses. In the worst-case scenario, this can result in a
            total loss of capital (up to 100%).
          </p>
        </li>
        <li>
          <p className="p">
            <b>Lockup risk:</b> LP token withdrawals are subject to a 24-hour
            delay. During this period, capital remains exposed to market
            movements and trader activity. xLP tokens have a 45-day unbinding
            period. This means you cannot access your capital for 45 days after
            requesting withdrawal, as so you are exposed to extended periods of
            price risk in the pool. In return for the extended lockup and added
            risk, xLP holders receive a larger share of fees and rewards.
          </p>
        </li>
      </ul>
    </>
  );
};

const sharpeRatioPercentage = (sharpeRatio: number) => {
  const scaled = sharpeRatio / 1e13;
  const clamp = Math.max(0, Math.min(3, scaled));
  return (clamp / 3) * 100;
};

const sharpeRatioAmount = (sharpeRatio: number) => {
  const scaled = sharpeRatio / 1e12;
  const clamp = Math.max(0, Math.min(30, Math.floor(scaled)));
  return BigInt(clamp);
};
